// components/TemplateLibrary.jsx
import React, { useEffect, useState } from "react";
import api from "../api";
import Card from "./ui/Card";
import CardContent from "./ui/CardContent";
import Input from "./ui/Input";
import Button from "./ui/Button";
import assemblyModule from "../templateAssembly";

const { assembleTemplate } = assemblyModule;

// A reusable prompt library. Templates are assembled at use-time from the
// composition catalog (reusable {{block:...}} mixins + adjustable {{slider:...}}
// knobs), so the settings the user picks below are injected into whatever
// template they use. `onUse` receives the assembled body; `currentInput` is the
// text offered when saving a new template.
export default function TemplateLibrary({ onUse, currentInput }) {
  const [visible, setVisible] = useState(false);
  const [curated, setCurated] = useState([]);
  const [saved, setSaved] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [sliderValues, setSliderValues] = useState({});
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!visible) return;
    const loadTemplates = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/templates");
        setCurated(res.data.curated || []);
        setSaved(res.data.saved || []);
        const bl = res.data.blocks || [];
        const sl = res.data.sliders || [];
        setBlocks(bl);
        setSliders(sl);
        // Seed slider choices from defaults the first time; keep user picks after.
        setSliderValues((prev) => {
          if (Object.keys(prev).length) return prev;
          const init = {};
          sl.forEach((s) => {
            init[s.key] = s.default;
          });
          return init;
        });
      } catch (err) {
        console.error("Error loading templates:", err);
        setError("Failed to load templates.");
      } finally {
        setLoading(false);
      }
    };
    loadTemplates();
  }, [visible]);

  const assemble = (body) => assembleTemplate(body, { blocks, sliders, sliderValues });

  const handleUse = (body) => {
    onUse(assemble(body));
    setVisible(false);
  };

  const handleSave = async () => {
    if (!title.trim() || !currentInput.trim()) {
      setError("Add a title and enter some prompt text to save.");
      return;
    }
    try {
      const res = await api.post("/templates", {
        title: title.trim(),
        body: currentInput,
      });
      setSaved((prev) => [res.data, ...prev]);
      setTitle("");
      setError("");
    } catch (err) {
      console.error("Error saving template:", err);
      setError("Failed to save template.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      setSaved((prev) => prev.filter((t) => t._id !== id));
    } catch (err) {
      console.error("Error deleting template:", err);
      setError("Failed to delete template.");
    }
  };

  const renderTemplate = (t, { deletable } = {}) => (
    <div key={t._id || t.id} className="border rounded p-2">
      <div className="flex justify-between items-start gap-2">
        <div>
          <p className="font-medium text-sm">{t.title}</p>
          {t.category && (
            <span className="inline-block text-xs bg-gray-200 text-gray-700 rounded px-2 py-0.5 mt-1">
              {t.category}
            </span>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={() => handleUse(t.body)}>Use</Button>
          {deletable && (
            <button
              onClick={() => handleDelete(t._id)}
              className="text-sm text-red-600 underline"
            >
              Delete
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500 whitespace-pre-wrap mt-2 line-clamp-3">
        {assemble(t.body)}
      </p>
    </div>
  );

  return (
    <div className="space-y-4">
      <Button onClick={() => setVisible(!visible)}>
        {visible ? "Hide Templates" : "Browse Templates"}
      </Button>

      {visible && (
        <Card>
          <CardContent>
            {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
            {loading ? (
              <p className="text-sm text-gray-500">Loading templates…</p>
            ) : (
              <div className="space-y-4">
                {sliders.length > 0 && (
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-1">Settings</h3>
                    <p className="text-xs text-gray-500 mb-2">
                      Injected into any template you use.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {sliders.map((s) => (
                        <label key={s.key} className="text-sm">
                          <span className="block font-medium mb-1">{s.label}</span>
                          <select
                            value={sliderValues[s.key] || s.default}
                            onChange={(e) =>
                              setSliderValues((prev) => ({
                                ...prev,
                                [s.key]: e.target.value,
                              }))
                            }
                            className="w-full border p-2 rounded"
                          >
                            {s.options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="max-h-96 overflow-y-auto space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Starter Templates</h3>
                    <div className="space-y-2">
                      {curated.map((t) => renderTemplate(t))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">My Templates</h3>
                    {saved.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        You haven't saved any templates yet.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {saved.map((t) => renderTemplate(t, { deletable: true }))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 border-t pt-4 space-y-2">
              <h3 className="font-semibold">Save current prompt as a template</h3>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Template title"
              />
              <Button onClick={handleSave}>Save Template</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
